import { useMutation } from '@apollo/client';
import React, { useState } from 'react';

import { SettingsImageUpload } from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import {
  useTenantLogo,
  useTenantName,
  useUploadGenericFile,
} from '@eluve/smart-blocks';

import { updateTenantDetailsMutation } from './operations';

export const TenantLogoSettings: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const { tenantLogoUrl } = useTenantLogo(tenantId);
  const tenantName = useTenantName(tenantId);
  const [isUploading, setIsUploading] = useState(false);
  const { uploadGenericFileToGCS } = useUploadGenericFile(tenantId);
  const toast = useToast();

  const [updateTenantDetails] = useMutation(updateTenantDetailsMutation, {
    onError: () => {
      toast.error('Failed to update organization logo');
    },
  });

  const handleFileSelect = async (file: File) => {
    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();

      if (!fileExt) {
        toast.error('Invalid file extension.');
        return;
      }

      const result = await uploadGenericFileToGCS({
        file,
        mimeType: 'image/*',
        fileExt,
      });

      if (result.url) {
        await updateTenantDetails({
          variables: {
            tenantId,
            logoUrl: result.url,
            name: tenantName,
          },
        });
      }
    } catch {
      toast.error('Failed to update organization logo.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsUploading(true);
      await updateTenantDetails({
        variables: {
          tenantId,
          logoUrl: '',
          name: tenantName,
        },
      });
    } catch {
      toast.error('Failed to delete organization logo.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <SettingsImageUpload
      src={tenantLogoUrl}
      alt="Organization logo"
      disabled={isUploading}
      onSelect={handleFileSelect}
      onDelete={handleDelete}
    />
  );
};
